import { createContext, useCallback, useContext, useEffect, useState, type FormEvent, type ReactNode } from "react";

// Posts to our own Go backend (/api/leads), which stores the lead and emails
// LEAD_NOTIFY_TO via the SMTP mailer. Same-origin in prod (Go serves the SPA).
const SUBMIT_ADDR = `${import.meta.env.VITE_API_BASE ?? ""}/api/leads`;

type BookDemoContextValue = { openBookDemo: () => void };

const BookDemoContext = createContext<BookDemoContextValue>({ openBookDemo: () => {} });

export function useBookDemo() {
  return useContext(BookDemoContext);
}

export function BookDemoProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [charCount, setCharCount] = useState(0);

  const openBookDemo = useCallback(() => {
    setStatus("idle");
    setErrorMsg("");
    setCharCount(0);
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = (data.get("website_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      return;
    }
    data.delete("website_alt");

    setStatus("submitting");
    setErrorMsg("");

    try {
      const payload = {
        email: ((data.get("email") as string) || "").trim(),
        message: ((data.get("message") as string) || "").trim(),
        source: "demo",
      };

      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let parsed: { ok?: boolean; error?: string } | null = null;
      try {
        parsed = JSON.parse(await res.text());
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
        setErrorMsg(parsed?.error || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  };

  return (
    <BookDemoContext.Provider value={{ openBookDemo }}>
      {children}

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="modal-backdrop absolute inset-0 bg-foreground-950/70 backdrop-blur-sm" onClick={close} />

          <div className="modal-card relative w-full max-w-md bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-950 hover:bg-background-100 rounded-md transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>

            {status === "success" ? (
              <div className="py-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-500/15 flex items-center justify-center text-primary-600">
                  <i className="ri-check-line text-2xl" />
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">Thanks — request received!</h3>
                <p className="text-sm text-foreground-600 mb-6">
                  We&apos;ll reach out shortly to schedule your 30-minute demo.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="btn-primary text-sm w-full"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-950 mb-1">
                  Book a demo
                </h3>
                <p className="text-sm text-foreground-600 mb-5">
                  Tell us how to reach you and we&apos;ll walk your team through the platform in 30 minutes.
                </p>

                <form
                  id="book-demo-form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="bd-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Work email
                      </label>
                      <input
                        id="bd-email"
                        type="email"
                        name="email"
                        required
                        autoComplete="email"
                        placeholder="you@company.com"
                        className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-shadow"
                      />
                    </div>

                    <div>
                      <label htmlFor="bd-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Message
                      </label>
                      <textarea
                        id="bd-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        placeholder="What are you building, and what would you like to cover?"
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-shadow resize-none"
                      />
                      <div className="flex justify-end mt-1">
                        <span className={`text-xs tabular-nums ${charCount >= 500 ? "text-accent-600" : "text-foreground-400"}`}>
                          {charCount}/500
                        </span>
                      </div>
                    </div>

                    {/* Honeypot */}
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="hp-field"
                    />

                    {status === "error" && (
                      <p className="text-sm text-accent-600 bg-accent-500/10 border border-accent-500/20 rounded-md px-3 py-2">
                        {errorMsg}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="btn-primary text-sm w-full disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          Sending…
                        </>
                      ) : (
                        "Request demo"
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </BookDemoContext.Provider>
  );
}